import axios from 'axios'
import { message } from 'antd'
import React, { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getCensusFileRows } from '../api/census-enrollment-platform-api-client'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { CensusFileRowTypeEnum } from '../api/enums/census-file-row-type.enum'
import DownloadCensusReportButton from '../components/DownloadCensusReportButton'
import { LoadingOverlay } from '../components/LoadingOverlay/LoadingOverlay'
import ReviewRecordsBase from '../components/ReviewRecordsBase'
import WithdrawSelectedButton from '../components/WithdrawSelectedButton'
import { CensusManagementPageProps } from '../types/census-management-page-props'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'

const ReviewRecordsDuplicateEmployee = (props: CensusManagementPageProps) => {
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { importPublicKey } = props.routeParams
  const queryClient = useQueryClient()
  const queryKey = ['duplicateRows', importPublicKey, searchTerm]

  const { data: allDuplicateRows = [], isError } = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      // current axios version (0.17.1) doesn't support AbortSignal natively; bridge it to a cancel token
      const source = axios.CancelToken.source()
      signal?.addEventListener('abort', () => source.cancel())
      return getCensusFileRows(
        {
          limit: 1,
          page: 1,
          publicKey: importPublicKey,
          type: [CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE],
          status: [CensusFileRowStatusEnum.IN_PROCESS],
          getAll: true,
          search: searchTerm || undefined,
        },
        { cancelToken: source.token }
      ).then(({ data }) =>
        data.map((d) => ({ ...d, ...d.rowData }) as CensusFileRowGridData)
      )
    },
  })

  useEffect(() => {
    if (isError)
      message.error('Failed to load all duplicate records for withdraw')
  }, [isError])

  return (
    <>
      <LoadingOverlay isLoading={loading} message="Loading..." />
      <ReviewRecordsBase
        tabKey={CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE}
        {...props}
        rowQueryParams={{
          censusFileRowStatuses: [CensusFileRowStatusEnum.IN_PROCESS],
          censusRowType: CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE,
        }}
        actionButton={({ censusRows, handleSubmit, counts }) => {
          const handleWithdrawSubmit = () => {
            queryClient.resetQueries({ queryKey })
            handleSubmit()
          }
          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <WithdrawSelectedButton
                publicKey={importPublicKey}
                selectedRows={allDuplicateRows}
                handleSubmit={handleWithdrawSubmit}
                setLoading={setLoading}
                type={CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE}
                allRows={censusRows}
                counts={counts}
              />
              <DownloadCensusReportButton
                publicKey={importPublicKey}
                rowType={CensusFileRowTypeEnum.DUPLICATE_EMPLOYEE}
              />
            </div>
          )
        }}
        showSearch
        onSearchChange={(term) => {
          if (term === searchTerm) return
          setSearchTerm(term) // key change clears data immediately + triggers refetch
        }}
      />
    </>
  )
}

export default ReviewRecordsDuplicateEmployee
